import React from 'react';
import { Box, Typography, Button, Grid, useTheme, useMediaQuery } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { colors, fonts } from '../../ColorFont/color';
import { url } from '../../ConstUrl/Url';
import {
    IconLeaf,
    IconAward,
    IconClock,
    IconWallet,
    IconPlus,
    IconUpload,
    IconTrendingUp,
    IconCurrencyRupee,
    IconChevronRight,
    IconInfoCircle,
    IconMapPin,
    IconX,
    IconEye,
    IconCalendar,
    IconBox,
    IconSeeding,
    IconClipboardList
} from '@tabler/icons-react';
import { Dialog, DialogTitle, DialogContent, IconButton, Divider, CircularProgress } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import NewHeaderNavWithSidebar from '../../components/HeaderNav/NewHeaderNavWithSidebar';
import { useUser } from '../../context/UserContext';
import CarbonCreditsAuthGuard from '../../components/CarbonCredits/CarbonCreditsAuthGuard';

/* 
===== UNIVERSAL TOKENS =====
Imported from currentTheme dynamically.
Theme variables added:
- carbon_bg_main
- carbon_bg_light
- carbon_bg_card
- carbon_text_main
- carbon_text_secondary
- carbon_shadow_card
- carbon_shadow_btn
- carbon_shadow_btn_outline
- carbon_gradient_earning
- carbon_shadow_earning
- carbon_text_earning_bg
- carbon_text_active_bg
- carbon_overlay
- carbon_border_dashed
============================
*/

/*
===== OLD UI (Commented) =====
(No old code existed for CarbonCreditsDashboard. 
This is a brand new screen based on new Figma UI.)
*/

/* ===== NEW FIGMA UI ===== */

const carbonTranslations = {
    en: {
        title: "Carbon Credits",
        subtitle: "Earn by sustainable farming",
        creditsEarned: "Credits Earned",
        pendingVerification: "Pending Verification",
        availableToSell: "Available to Sell",
        walletBalance: "Wallet Balance",
        startProject: "Start Project",
        uploadProof: "Upload Proof",
        sellCredits: "Sell Credits",
        withdraw: "Withdraw",
        totalEarnings: "Total Earnings",
        creditsSold: "Credits Sold",
        activeProjects: "Active Projects",
        yourProjects: "Your Projects",
        viewAll: "View All",
        noProjects: "No Carbon Projects Yet",
        startEarning: "Start earning carbon credits through sustainable farming practices",
        startFirstProject: "Start Your First Project",
        addAnotherProject: "Add Another Project",
        myProofs: "My Proofs",
        sellCredit: "Sell Credit",
        viewProofs: "View Proofs",
        tCo2: "tCO₂"
    },
    hi: {
        title: "कार्बन क्रेडिट्स",
        subtitle: "टिकाऊ खेती से कमाएं",
        creditsEarned: "क्रेडिट अर्जित किए",
        pendingVerification: "सत्यापन लंबित",
        availableToSell: "बेचने के लिए उपलब्ध",
        walletBalance: "वॉलेट बैलेंस",
        startProject: "प्रोजेक्ट शुरू करें",
        uploadProof: "प्रमाण अपलोड करें",
        sellCredits: "क्रेडिट बेचें",
        withdraw: "पैसे निकालें",
        totalEarnings: "कुल कमाई",
        creditsSold: "बेचे गए क्रेडिट",
        activeProjects: "सक्रिय प्रोजेक्ट",
        yourProjects: "आपके प्रोजेक्ट",
        viewAll: "सभी देखें",
        noProjects: "अभी तक कोई कार्बन प्रोजेक्ट नहीं",
        startEarning: "टिकाऊ खेती के माध्यम से कार्बन क्रेडिट अर्जित करना शुरू करें",
        startFirstProject: "अपना पहला प्रोजेक्ट शुरू करें",
        addAnotherProject: "एक और प्रोजेक्ट जोड़ें",
        myProofs: "मेरे प्रमाण",
        sellCredit: "क्रेडिट बेचें",
        viewProofs: "प्रमाण देखें",
        tCo2: "tCO₂"
    },
    mr: {
        title: "कार्बन क्रेडिट्स",
        subtitle: "शाश्वत शेतीतून कमवा",
        creditsEarned: "मिळवलेले क्रेडिट्स",
        pendingVerification: "पडताळणी प्रलंबित",
        availableToSell: "विक्रीसाठी उपलब्ध",
        walletBalance: "वॉलेट शिल्लक",
        startProject: "प्रकल्प सुरू करा",
        uploadProof: "पुरावा अपलोड करा",
        sellCredits: "क्रेडिट्स विका",
        withdraw: "पैसे काढा",
        totalEarnings: "एकूण कमाई",
        creditsSold: "विकलेले क्रेडिट्स",
        activeProjects: "सक्रिय प्रकल्प",
        yourProjects: "तुमचे प्रकल्प",
        viewAll: "सर्व पहा",
        noProjects: "अद्याप कोणतेही कार्बन प्रकल्प नाहीत",
        startEarning: "शाश्वत शेती पद्धतींद्वारे कार्बन क्रेडिट्स मिळवणे सुरू करा",
        startFirstProject: "तुमचा पहिला प्रकल्प सुरू करा",
        addAnotherProject: "आणखी एक प्रकल्प जोडा",
        myProofs: "माझे पुरावे",
        sellCredit: "क्रेडिट विका",
        viewProofs: "पुरावे पहा",
        tCo2: "tCO₂"
    }
};

const MetricCard = ({ icon, value, label, unit, iconBg, iconColor }) => {
    return (
        <Box sx={{
            background: colors.carbon_bg_card || '#FFFFFF',
            boxShadow: colors.carbon_shadow_card || '0px 8px 30px 0px rgba(0,0,0,0.04)',
            borderRadius: '12px',
            padding: '16px',
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            height: '100%',
        }}>
            <Box sx={{
                width: '40px',
                height: '40px',
                borderRadius: '12px',
                background: iconBg,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: iconColor
            }}>
                {icon}
            </Box>
            <Box sx={{ mt: '8px' }}>
                <Typography sx={{
                    fontFamily: fonts.manrope || fonts.poppins,
                    fontWeight: 700,
                    fontSize: '24px',
                    lineHeight: '32px',
                    color: colors.carbon_text_main || '#0F1729',
                    display: 'flex',
                    alignItems: 'baseline',
                    gap: '4px'
                }}>
                    {value}
                    {unit && (
                        <Typography component="span" sx={{
                            fontFamily: fonts.manrope || fonts.poppins,
                            fontWeight: 400,
                            fontSize: '14px',
                            color: colors.carbon_text_secondary || '#65758B'
                        }}>
                            {unit}
                        </Typography>
                    )}
                </Typography>
                <Typography sx={{
                    fontFamily: fonts.publicSans || fonts.poppins,
                    fontWeight: 400,
                    fontSize: '12px',
                    lineHeight: '16px',
                    color: colors.carbon_text_secondary || '#65758B',
                    mt: '4px'
                }}>
                    {label}
                </Typography>
            </Box>
        </Box>
    );
};

const ProjectCard = ({ project, onInfoClick, tLocal }) => {
    const navigate = useNavigate();
    return (
        <Box sx={{
            p: '20px',
            background: colors.carbon_bg_card || '#FFFFFF',
            borderRadius: '16px',
            border: `1px solid ${colors.carbon_border_solid || '#E0E5EB'}`,
            boxShadow: colors.carbon_shadow_card || '0px 2px 4px rgba(0,0,0,0.05)',
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            gap: '12px',
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            cursor: 'pointer',
            flexShrink: 0,
            '&:hover': {
                boxShadow: '0px 12px 24px rgba(0,0,0,0.1)',
                transform: 'translateY(-4px)',
                borderColor: colors.carbon_bg_main || '#00B4D8'
            }
        }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                    <Typography sx={{
                        fontFamily: fonts.manrope || fonts.poppins,
                        fontWeight: 700,
                        fontSize: '15px',
                        color: colors.carbon_text_main || '#0F1729',
                        mb: '4px',
                        display: '-webkit-box',
                        WebkitLineClamp: 1,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                    }}>
                        {project.projectName || (project.village ? `${project.village} Project` : 'Carbon Project')}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <IconMapPin size={14} color="#65758B" />
                        <Typography sx={{
                            fontFamily: fonts.publicSans || fonts.poppins,
                            fontSize: '11px',
                            color: colors.carbon_text_secondary || '#65758B'
                        }}>
                            {project.district}
                        </Typography>
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: '8px' }}>
                    <Button
                        onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/carbon-credits/my-proofs?projectId=${project._id}`);
                        }}
                        size="small"
                        sx={{
                            textTransform: 'none',
                            fontFamily: fonts.publicSans || fonts.poppins,
                            fontWeight: 600,
                            fontSize: '11px',
                            color: colors.carbon_bg_main || '#00B4D8',
                            background: (colors.carbon_bg_main || '#00B4D8') + '10',
                            borderRadius: '8px',
                            px: '12px',
                            '&:hover': { background: (colors.carbon_bg_main || '#00B4D8') + '20' }
                        }}
                    >
                        {tLocal.viewProofs}
                    </Button>
                    <IconButton 
                        onClick={(e) => {
                            e.stopPropagation();
                            onInfoClick(project);
                        }}
                        sx={{ 
                            color: colors.carbon_text_secondary || '#65758B',
                            background: '#F1F5F9',
                            padding: '8px',
                            borderRadius: '10px',
                            '&:hover': { background: '#E2E8F0' }
                        }}
                    >
                        <IconEye size={18} stroke={2} />
                    </IconButton>
                </Box>
            </Box>

            <Grid container spacing={1}>
                <Grid item xs={6}>
                    <Typography sx={{ fontSize: '10px', color: '#65758B', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.4px', mb: '4px' }}>
                        Area
                    </Typography>
                    <Typography sx={{ fontWeight: 700, fontSize: '14px', color: '#0F1729', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <IconBox size={14} color={colors.carbon_bg_main} /> {project.landSize} {project.unit}
                    </Typography>
                </Grid>
                <Grid item xs={6}>
                    <Typography sx={{ fontSize: '10px', color: '#65758B', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.4px', mb: '4px' }}>
                        Status
                    </Typography>
                    <Box sx={{ 
                        display: 'inline-flex', 
                        px: '8px', 
                        py: '2px', 
                        borderRadius: '6px', 
                        background: project.status === 'Verified' ? colors.primary_rgba_8 : '#FFF3E0',
                        color: project.status === 'Verified' ? '#2E7D32' : '#F57C00',
                        fontSize: '10px',
                        fontWeight: 800
                    }}>
                        {project.status || 'Under Review'}
                    </Box>
                </Grid>
            </Grid>

            <Divider sx={{ borderColor: '#F1F5F9', my: '4px' }} />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography sx={{ fontSize: '12px', color: '#65758B', fontWeight: 500 }}>
                    Est. Carbon Credits
                </Typography>
                <Typography sx={{ 
                    fontFamily: fonts.manrope || fonts.poppins,
                    fontWeight: 800, 
                    fontSize: '18px', 
                    color: colors.carbon_bg_main || '#00B4D8' 
                }}>
                    +{project.estimatedCredits} <span style={{ fontSize: '11px', fontWeight: 600 }}>{tLocal.tCo2}</span>
                </Typography>
            </Box>
        </Box>
    );
};

const ActionButton = ({ icon, label, primary, onClick }) => {
    return (
        <Button
            onClick={onClick}
            sx={{
                width: '100%',
                height: '76px',
                borderRadius: '14px',
                textTransform: 'none',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '8px',
                background: primary ? (colors.carbon_bg_main || '#00B4D8') : 'transparent',
                color: primary ? '#FFFFFF' : (colors.carbon_text_main || '#0F1729'),
                border: primary ? 'none' : `2px solid ${colors.carbon_border_dashed || '#E0E5EB'}`,
                boxShadow: primary ? (colors.carbon_shadow_btn || '0px 1px 3px rgba(0,0,0,0.1)') : (colors.carbon_shadow_btn_outline || '0px 1px 2px rgba(0,0,0,0.05)'),
                '&:hover': {
                    background: primary ? '#009DC0' : 'rgba(0,0,0,0.02)',
                    border: primary ? 'none' : `2px solid ${colors.carbon_border_dashed || '#E0E5EB'}`,
                }
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {icon}
            </Box>
            <Typography sx={{
                fontFamily: fonts.publicSans || fonts.poppins,
                fontWeight: 500,
                fontSize: '12px',
                lineHeight: '16px',
            }}>
                {label}
            </Typography>
        </Button>
    );
};

const CarbonCreditsDashboard = ({ onStartProject, onUploadProof, onMyWallet, onSellCredits, onMyProofs, userData: propUserData }) => {
    const { userData, firebaseUser, loading } = useUser();
    const { i18n } = useTranslation();
    const currentLang = i18n.language || 'en';
    const tLocal = carbonTranslations[currentLang] || carbonTranslations['en'];

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));
    const navigate = useNavigate();

    const handleStartProject = () => {
        if (onStartProject) onStartProject();
        else navigate('/carbon-credits/farm-details');
    };

    const handleUploadProof = () => {
        if (onUploadProof) onUploadProof();
        else navigate('/carbon-credits/upload-proof');
    };

    const handleWithdraw = () => {
        if (onMyWallet) onMyWallet();
        else navigate('/carbon-credits/wallet');
    };

    const handleSellCredits = () => {
        if (onSellCredits) onSellCredits();
        else navigate('/carbon-credits/sell-credit');
    };

    const handleMyProofs = () => {
        if (onMyProofs) onMyProofs();
        else navigate('/carbon-credits/my-proofs');
    };

    const [projects, setProjects] = React.useState([]);
    const [stats, setStats] = React.useState({
        totalEarned: "0.0",
        pending: "0.0",
        available: "0.0",
        wallet: "₹0",
        activeCount: 0
    });
    const [selectedProject, setSelectedProject] = React.useState(null);
    const [openModal, setOpenModal] = React.useState(false);
    const [selectedProjectProofs, setSelectedProjectProofs] = React.useState([]);
    const [loadingProofs, setLoadingProofs] = React.useState(false);

    const handleInfoClick = (project) => {
        setSelectedProject(project);
        setOpenModal(true);
        setLoadingProofs(true);
        fetch(`${url}/api/carbon-credits/proofs/project/${project._id}`)
            .then(res => res.json())
            .then(data => {
                setSelectedProjectProofs(data.proofs || []);
                setLoadingProofs(false);
            })
            .catch(err => {
                console.error("Error fetching proofs:", err);
                setLoadingProofs(false);
            });
    };

    const handleCloseModal = () => {
        setOpenModal(false);
        setSelectedProject(null);
    };

    React.useEffect(() => {
        if (!userData?._id) return;

        const userId = userData._id;

        // Fetch projects list and summary stats in parallel
        Promise.all([
            fetch(`${url}/api/carbon-credits/projects/${userId}`).then(r => r.json()),
            fetch(`${url}/api/carbon-credits/summary/${userId}`).then(r => r.json())
        ])
            .then(([projectsData, summaryData]) => {
                // Map projects — flatten nested farmDetails fields for the ProjectCard component
                const records = (projectsData.projects || []).map(p => ({
                    ...p,
                    district: p.farmDetails?.district || '',
                    state: p.farmDetails?.state || '',
                    village: p.farmDetails?.city || p.farmDetails?.village || '',
                    landSize: p.farmDetails?.landSize || '',
                    unit: p.farmDetails?.unit || '',
                }));
                setProjects(records);

                // Map summary stats
                const s = summaryData.summary || {};
                setStats({
                    totalEarned: (s.creditsEarned ?? 0).toFixed(1),
                    pending: (s.verificationsPending ?? 0).toFixed(1),
                    available: (s.availableToSell ?? 0).toFixed(1),
                    wallet: `₹${(s.walletBalance ?? 0).toLocaleString('en-IN')}`,
                    activeCount: records.length
                });
            })
            .catch(err => console.error("Error fetching carbon stats:", err));
    }, [userData]);

    return (
        <CarbonCreditsAuthGuard 
            userData={userData} 
            firebaseUser={firebaseUser} 
            loading={loading}
        >
            <Box sx={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: '32px',
                // Local padding removed — handled by NewHeaderNavWithSidebar layout
            }}>
                {/* Header Section */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Box sx={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '12px',
                        backgroundColor: colors.carbon_bg_light || '#B2EBF2',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#00838F'
                    }}>
                        <IconLeaf size={20} stroke={2} />
                    </Box>
                    <Box>
                        <Typography sx={{
                            fontFamily: fonts.manrope || fonts.poppins,
                            fontWeight: 700,
                            fontSize: '24px',
                            lineHeight: '32px',
                            letterSpacing: '-0.6px',
                            color: colors.carbon_text_main || '#0F1729'
                        }}>
                            {tLocal.title}
                        </Typography>
                        <Typography sx={{
                            fontFamily: fonts.publicSans || fonts.poppins,
                            fontWeight: 400,
                            fontSize: '14px',
                            lineHeight: '20px',
                            color: colors.carbon_text_secondary || '#65758B'
                        }}>
                            {tLocal.subtitle}
                        </Typography>
                    </Box>
                </Box>

            {/* Metrics Grid */}
            <Grid container spacing={2}>
                <Grid item xs={6} sm={6} md={3}>
                    <MetricCard
                        icon={<IconAward size={20} />}
                        value={stats.totalEarned}
                        unit={tLocal.tCo2}
                        label={tLocal.creditsEarned}
                        iconBg="rgba(0, 180, 216, 0.15)"
                        iconColor="#00B4D8"
                    />
                </Grid>
                <Grid item xs={6} sm={6} md={3}>
                    <MetricCard
                        icon={<IconClock size={20} />}
                        value={stats.pending}
                        unit={tLocal.tCo2}
                        label={tLocal.pendingVerification}
                        iconBg="#FFF3E0"
                        iconColor="#F57C00"
                    />
                </Grid>
                <Grid item xs={6} sm={6} md={3}>
                    <MetricCard
                        icon={<IconLeaf size={20} />}
                        value={stats.available}
                        unit={tLocal.tCo2}
                        label={tLocal.availableToSell}
                        iconBg="colors.primary_rgba_8"
                        iconColor="#2E7D32"
                    />
                </Grid>
                <Grid item xs={6} sm={6} md={3}>
                    <MetricCard
                        icon={<IconWallet size={20} />}
                        value={stats.wallet}
                        label={tLocal.walletBalance}
                        iconBg="colors.primary_rgba_8"
                        iconColor="#2E7D32"
                    />
                </Grid>
            </Grid>

            {/* Action Buttons */}
            <Grid container spacing={2}>
                <Grid item xs={6} sm={6} md={3}>
                    <ActionButton icon={<IconPlus size={20} />} label={tLocal.startProject} primary={true} onClick={handleStartProject} />
                </Grid>
                <Grid item xs={6} sm={6} md={3}>
                    <ActionButton icon={<IconUpload size={20} />} label={tLocal.uploadProof} onClick={handleUploadProof} />
                </Grid>
                <Grid item xs={6} sm={6} md={3}>
                    <ActionButton icon={<IconTrendingUp size={20} />} label={tLocal.sellCredit} onClick={handleSellCredits} />
                </Grid>
                <Grid item xs={6} sm={6} md={3}>
                    <ActionButton icon={<IconCurrencyRupee size={20} />} label={tLocal.withdraw} onClick={handleWithdraw} />
                </Grid>
            </Grid>

            {/* Total Earnings Card */}
            <Box sx={{
                width: '100%',
                background: colors.carbon_gradient_earning || 'linear-gradient(135deg, #1A4D2E 0%, #10451D 100%)',
                boxShadow: colors.carbon_shadow_earning || '0px 12px 40px 0px rgba(0, 0, 0, 0.08)',
                borderRadius: '12px',
                padding: isMobile ? '20px' : '24px',
                position: 'relative',
                overflow: 'hidden',
                display: 'flex',
                alignItems: 'center'
            }}>
                <Box sx={{
                    position: 'absolute',
                    width: '128px',
                    height: '128px',
                    borderRadius: '50%',
                    background: colors.carbon_overlay || 'rgba(255, 255, 255, 0.05)',
                    top: '-64px',
                    right: '-20px'
                }} />

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', zIndex: 1 }}>
                    <Box>
                        <Typography sx={{
                            fontFamily: fonts.publicSans || fonts.poppins,
                            fontWeight: 400,
                            fontSize: '14px',
                            color: colors.carbon_text_earning_bg || '#C8E6C9',
                            mb: '4px'
                        }}>
                            {tLocal.totalEarnings}
                        </Typography>
                        <Typography sx={{
                            fontFamily: fonts.manrope || fonts.poppins,
                            fontWeight: 700,
                            fontSize: '36px',
                            lineHeight: '40px',
                            color: '#FFFFFF'
                        }}>
                            ₹0
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: isMobile ? '16px' : '32px' }}>
                        <Box sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            borderRight: '1px solid rgba(255,255,255,0.2)',
                            paddingRight: isMobile ? '16px' : '32px'
                        }}>
                            <Typography sx={{
                                fontFamily: fonts.publicSans || fonts.poppins,
                                fontWeight: 400,
                                fontSize: '14px',
                                color: colors.carbon_text_active_bg || '#A5D6A7'
                            }}>
                                {tLocal.creditsSold}
                            </Typography>
                            <Typography sx={{
                                fontFamily: fonts.publicSans || fonts.poppins,
                                fontWeight: 700,
                                fontSize: '14px',
                                color: '#FFFFFF'
                            }}>
                                0.0 {tLocal.tCo2}
                            </Typography>
                        </Box>
                        <Box sx={{
                            display: 'flex',
                            flexDirection: 'column'
                        }}>
                            <Typography sx={{
                                fontFamily: fonts.publicSans || fonts.poppins,
                                fontWeight: 400,
                                fontSize: '14px',
                                color: colors.carbon_text_active_bg || '#A5D6A7'
                            }}>
                                {tLocal.activeProjects}
                            </Typography>
                            <Typography sx={{
                                fontFamily: fonts.publicSans || fonts.poppins,
                                fontWeight: 700,
                                fontSize: '14px',
                                color: '#FFFFFF'
                            }}>
                                {stats.activeCount}
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            </Box>

            {/* Your Projects Section */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography sx={{
                        fontFamily: fonts.manrope || fonts.poppins,
                        fontWeight: 600,
                        fontSize: '18px',
                        lineHeight: '28px',
                        letterSpacing: '-0.45px',
                        color: colors.carbon_text_main || '#0F1729'
                    }}>
                        {tLocal.yourProjects}
                    </Typography>
                    <Button
                        onClick={() => navigate('/carbon-credits/all-projects')}
                        endIcon={<IconChevronRight size={16} />}
                        sx={{
                            textTransform: 'none',
                            fontFamily: fonts.publicSans || fonts.poppins,
                            fontWeight: 500,
                            fontSize: '12px',
                            color: colors.carbon_text_main || '#0F1729',
                            padding: 0,
                            '&:hover': { background: 'transparent', textDecoration: 'underline' }
                        }}
                    >
                        {tLocal.viewAll}
                    </Button>
                </Box>

                {projects.length > 0 ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
                        <Box sx={{ 
                            display: 'flex', 
                            flexDirection: 'column',
                            gap: '16px', 
                        }}>
                             {projects.slice(0, 3).map((proj) => (
                                <ProjectCard project={proj} onInfoClick={handleInfoClick} tLocal={tLocal} key={proj._id} />
                            ))}
                        </Box>
                        <Button
                            onClick={handleStartProject}
                            startIcon={<IconPlus size={18} />}
                            sx={{
                                background: colors.carbon_bg_main || '#00B4D8',
                                color: '#FFFFFF',
                                borderRadius: '14px',
                                padding: '10px 32px',
                                textTransform: 'none',
                                fontFamily: fonts.publicSans || fonts.poppins,
                                fontWeight: 600,
                                fontSize: '15px',
                                boxShadow: '0px 4px 12px rgba(0, 180, 216, 0.2)',
                                '&:hover': {
                                    background: '#009DC0',
                                    transform: 'scale(1.02)'
                                },
                                transition: 'all 0.2s ease',
                                width: 'fit-content',
                                alignSelf: 'center',
                                mt: '8px'
                            }}
                        >
                            {tLocal.addAnotherProject}
                        </Button>
                    </Box>
                ) : (
                    <Box sx={{
                        width: '100%',
                        minHeight: '260px',
                        borderRadius: '16px',
                        border: `2px dashed ${colors.carbon_border_dashed || '#E2E8F0'}`,
                        backgroundColor: 'rgba(248, 250, 252, 0.4)',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '24px',
                        padding: '40px 24px'
                    }}>
                        <Box sx={{
                            width: '64px',
                            height: '64px',
                            borderRadius: '50%',
                            background: 'rgba(0, 180, 216, 0.1)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: colors.carbon_bg_main || '#00B4D8'
                        }}>
                            <IconLeaf size={32} stroke={1.5} />
                        </Box>

                        <Box sx={{ textAlign: 'center' }}>
                            <Typography sx={{
                                fontFamily: fonts.manrope || fonts.poppins,
                                fontWeight: 700,
                                fontSize: '18px',
                                color: colors.carbon_text_main || '#0F1729',
                                mb: '8px'
                            }}>
                                {tLocal.noProjects}
                            </Typography>
                            <Typography sx={{
                                fontFamily: fonts.publicSans || fonts.poppins,
                                fontWeight: 400,
                                fontSize: '14px',
                                lineHeight: '22px',
                                color: colors.carbon_text_secondary || '#64748B',
                                maxWidth: '380px',
                                margin: '0 auto'
                            }}>
                                {tLocal.startEarning}
                            </Typography>
                        </Box>

                        <Button
                            onClick={handleStartProject}
                            startIcon={<IconPlus size={18} />}
                            sx={{
                                background: colors.carbon_bg_main || '#00B4D8',
                                color: '#FFFFFF',
                                borderRadius: '14px',
                                padding: '10px 32px',
                                textTransform: 'none',
                                fontFamily: fonts.publicSans || fonts.poppins,
                                fontWeight: 600,
                                fontSize: '15px',
                                boxShadow: '0px 4px 12px rgba(0, 180, 216, 0.2)',
                                '&:hover': {
                                    background: '#009DC0',
                                    transform: 'scale(1.02)'
                                },
                                transition: 'all 0.2s ease'
                            }}
                        >
                            {tLocal.startFirstProject}
                        </Button>
                    </Box>
                )}
            </Box>
        </Box>

        {/* Project Details Modal */}
        <Dialog 
            open={openModal} 
            onClose={handleCloseModal}
            fullScreen={true}
            PaperProps={{
                sx: { borderRadius: 0 }
            }}
        >
            <DialogTitle sx={{ 
                px: '28px',
                pt: '24px',
                pb: '20px',
                borderBottom: `1px solid ${colors.carbon_border_solid || '#E0E5EB'}`,
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                fontFamily: fonts.manrope || fonts.poppins,
                fontWeight: 700,
                fontSize: '20px',
                color: colors.carbon_text_main || '#0F1729',
            }}>
                Project Details
                <IconButton onClick={handleCloseModal} sx={{ color: '#65758B', p: '6px' }}>
                    <IconX size={22} />
                </IconButton>
            </DialogTitle>
            <DialogContent sx={{ px: '28px', pt: '28px !important', pb: '28px', maxHeight: '75vh' }}>
                {selectedProject && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
                        {/* Project Name */}
                        <Box sx={{ display: 'flex', gap: '16px', alignItems: 'center', pb: '20px' }}>
                            <Box sx={{ 
                                width: '52px', height: '52px', borderRadius: '14px', flexShrink: 0,
                                backgroundColor: colors.carbon_bg_light || '#B2EBF2',
                                display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#00838F'
                            }}>
                                <IconLeaf size={24} />
                            </Box>
                            <Box>
                                <Typography sx={{ fontSize: '11px', color: '#94A3B8', mb: '4px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.6px' }}>Project Name</Typography>
                                <Typography sx={{ fontWeight: 700, fontSize: '18px', color: colors.carbon_text_main, lineHeight: 1.3 }}>{selectedProject.projectName || (selectedProject.village ? `${selectedProject.village} Project` : 'Carbon Project')}</Typography>
                            </Box>
                        </Box>

                        <Divider sx={{ borderColor: '#F1F5F9', mx: '-28px' }} />

                        {/* Farm Location */}
                        <Box sx={{ display: 'flex', gap: '14px', py: '20px', borderBottom: '1px solid #F1F5F9' }}>
                            <IconMapPin size={20} color="#94A3B8" style={{ marginTop: '2px', flexShrink: 0 }} />
                            <Box>
                                <Typography sx={{ fontSize: '11px', color: '#94A3B8', mb: '6px', fontWeight: 700, letterSpacing: '0.6px', textTransform: 'uppercase' }}>FARM LOCATION</Typography>
                                <Typography sx={{ fontWeight: 500, fontSize: '15px', color: colors.carbon_text_main || '#0F1729', lineHeight: 1.5 }}>{selectedProject.village}, {selectedProject.district}, {selectedProject.state}</Typography>
                            </Box>
                        </Box>

                        {/* Stats grid */}
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0', borderRadius: '14px', border: '1px solid #F1F5F9', overflow: 'hidden' }}>
                            {[
                                { icon: <IconBox size={18} color={colors.carbon_bg_main || '#00B4D8'} />, label: 'LAND SIZE',  value: `${selectedProject.landSize} ${selectedProject.unit}` },
                                { icon: <IconAward size={18} color={colors.carbon_bg_main || '#00B4D8'} />, label: 'OWNERSHIP', value: selectedProject.ownershipType || 'N/A' },
                                { icon: <IconCalendar size={18} color={colors.carbon_bg_main || '#00B4D8'} />, label: 'DURATION',  value: selectedProject.projectDuration || 'N/A' },
                                { icon: <IconTrendingUp size={18} color={selectedProject.status === 'Verified' ? '#059669' : '#F57C00'} />, label: 'STATUS',    value: selectedProject.status || 'Under Review', valueColor: selectedProject.status === 'Verified' ? '#059669' : '#F57C00' },
                            ].map((item, idx) => (
                                <Box key={idx} sx={{
                                    p: '16px 18px',
                                    background: idx % 2 === 0 ? '#FAFBFC' : '#FFFFFF',
                                    borderRight: idx % 2 === 0 ? '1px solid #F1F5F9' : 'none',
                                    borderBottom: idx < 2 ? '1px solid #F1F5F9' : 'none',
                                }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', mb: '6px' }}>
                                        {item.icon}
                                        <Typography sx={{ fontSize: '10px', color: '#94A3B8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{item.label}</Typography>
                                    </Box>
                                    <Typography sx={{ fontWeight: 700, fontSize: '14px', color: item.valueColor || (colors.carbon_text_main || '#0F1729') }}>
                                        {item.value}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>

                        {/* Selected Practices */}
                        <Box sx={{ pt: '4px' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mb: '14px' }}>
                                <IconSeeding size={18} color={colors.carbon_bg_main || '#00B4D8'} />
                                <Typography sx={{ fontSize: '11px', color: '#94A3B8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.6px' }}>Selected Practices</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                {selectedProject.practices?.map((p, i) => (
                                    <Box key={i} sx={{ 
                                        px: '14px', py: '6px', borderRadius: '50px', 
                                        background: (colors.carbon_bg_main || '#00B4D8') + '12',
                                        fontSize: '12px', fontWeight: 600,
                                        color: colors.carbon_bg_main || '#00838F',
                                        border: `1px solid ${(colors.carbon_bg_main || '#00B4D8') + '30'}`,
                                    }}>
                                        {p}
                                    </Box>
                                ))}
                                {(!selectedProject.practices || selectedProject.practices.length === 0) && (
                                    <Typography sx={{ fontSize: '14px', color: '#94A3B8', fontStyle: 'italic' }}>No practices listed</Typography>
                                )}
                            </Box>
                        </Box>

                        {/* Estimated Credits */}
                        <Box sx={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center',
                            p: '20px 24px', 
                            borderRadius: '16px', 
                            background: `linear-gradient(135deg, ${colors.carbon_bg_light || '#E0F7FA'} 0%, #FFFFFF 100%)`,
                            border: `1px solid ${colors.carbon_border_solid || '#E0E5EB'}`,
                            mb: '40px'
                        }}>
                            <Box>
                                <Typography sx={{ fontWeight: 700, color: colors.carbon_text_main, fontSize: '15px', mb: '2px' }}>Estimated Carbon Credits</Typography>
                                <Typography sx={{ fontSize: '12px', color: '#65758B' }}>Based on current farming data</Typography>
                            </Box>
                            <Typography sx={{ 
                                fontFamily: fonts.manrope || fonts.poppins,
                                fontWeight: 800, 
                                fontSize: '24px', 
                                color: colors.carbon_bg_main || '#00B4D8' 
                            }}>
                                +{selectedProject.estimatedCredits} <span style={{ fontSize: '14px', fontWeight: 600 }}>{tLocal.tCo2}</span>
                            </Typography>
                        </Box>

                        {/* Upload Proof Button (if no proofs) */}
                        {!loadingProofs && selectedProjectProofs.length === 0 && (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', alignItems: 'center' }}>
                                <Button
                                    onClick={() => {
                                        handleCloseModal();
                                        navigate(`/carbon-credits/upload-proof?projectId=${selectedProject._id}`);
                                    }}
                                    variant="contained"
                                    startIcon={<IconPlus size={20} />}
                                    sx={{
                                        background: colors.carbon_bg_main || '#00B4D8',
                                        color: '#FFFFFF',
                                        borderRadius: '14px',
                                        padding: '12px 48px',
                                        textTransform: 'none',
                                        fontFamily: fonts.publicSans || fonts.poppins,
                                        fontWeight: 700,
                                        fontSize: '16px',
                                        boxShadow: '0px 8px 16px rgba(0, 180, 216, 0.2)',
                                        '&:hover': {
                                            background: '#009DC0',
                                            transform: 'translateY(-2px)',
                                            boxShadow: '0px 12px 20px rgba(0, 180, 216, 0.3)',
                                        },
                                        transition: 'all 0.3s ease',
                                        width: isMobile ? '100%' : 'fit-content'
                                    }}
                                >
                                    Upload Project Proofs
                                </Button>
                                <Typography sx={{ fontSize: '13px', color: '#94A3B8', textAlign: 'center' }}>
                                    Adding proof helps in faster project verification
                                </Typography>
                            </Box>
                        )}
                        {loadingProofs && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                                <CircularProgress size={24} />
                            </Box>
                        )}
                    </Box>
                )}
            </DialogContent>
        </Dialog>
        </CarbonCreditsAuthGuard>
    );
};

export default CarbonCreditsDashboard;
